import {ISimplePresentation, ISimplePresentationRunnerController} from "../SimplePresentationRunner";
import {Game} from "../Game";
import {gsap} from "gsap";
import {IMonster, IMonsterWalk, IPosition, MONSTER_EXPRESSION, MONSTER_WEAPON_ACTIONS} from "../server/IData";
import {Symbol} from "../reels/Symbol";
import {MonsterSymbol} from "../reels/MonsterSymbol";
import {Sound} from "../sound/Sound";
import {GameMode} from "@nolimitcity/slot-game/bin/flowcontrol/GameMode";
import {APISetting} from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";
import {MathHelper} from "@nolimitcity/slot-game/bin/utils/MathHelper";

export class AirstrikeRunner implements ISimplePresentation {
    private _isAbort: boolean;

    public shouldRun(): boolean {
        return Game.model.gameData.airStrikeRocketCoordinates && Game.model.gameData.airStrikeRocketCoordinates.length > 0 && !Game.model.gameData.suspendedAction;
    }

    public start(controller: ISimplePresentationRunnerController): void {
        const featureTl: gsap.core.Timeline = gsap.timeline({
                onComplete: () => {
                    controller.finish();
                }
            }),
            bigExplodeTl: gsap.core.Timeline = gsap.timeline(),
            monsters: IMonster[] = Game.model.gameData.monsters,
            airstrikes: IMonsterWalk[] = Game.model.gameData.airStrikeRocketCoordinates,
            carriedSymbolUserNode: IPosition = Game.model.gameData.carriedSymbolUserNode,
            {struckMonsters} = this.getStruckMonsters(airstrikes),
            monstersToExplode: string[] = [],
            turnDuration: number = Game.apiPlugin.settings.get(APISetting.FAST_SPIN) ? 0.2 / 2 : 0.2;
        
        this._isAbort = false;

        airstrikes.forEach((airstrike: IMonsterWalk, idx: number) => {
            Game.getMonstersToExplode(airstrike.x, 0, Game.ACTIVE_ROWS_NUM, monstersToExplode);
            for (let reelId: number = 0; reelId < Game.ACTIVE_REELS_NUM; reelId++) {
                Game.getMonstersToExplode(reelId, airstrike.y, airstrike.y + 1, monstersToExplode);
            }
        });

        featureTl.add(() => {
            controller.setSkipCallback(() => {
                this.onSkip(featureTl);
            });
        });

        for (let ctr: number = 0; ctr < monsters.length; ctr++) {
            const monster: IMonster = monsters[ctr];
            if (!monster.path || monster.path.length === 0) {
                continue;
            }

            if (monster.path[0].x === carriedSymbolUserNode.x && monster.path[0].y === carriedSymbolUserNode.y) {
                const monsterSymbol: MonsterSymbol = Game.reelsView.reelModels[monster.path[0].x].getMonsterSymbolAt(monster.symbol);

                featureTl.add([
                    () => {
                        if (monsterSymbol && monsterSymbol.parent) {
                            monsterSymbol.parent.addChild(monsterSymbol);
                        }
                    },
                    monsterSymbol.turnMonsterTowardsTarget(monster.path[0].x, Game.ACTIVE_REELS_NUM / 2, turnDuration)
                ]);
                featureTl.add(monsterSymbol.throwAirstrike());

                featureTl.addLabel("scared", "<0.5");
                featureTl.addLabel("hitAction");

                Game.monsterReelsView.monsterSymbolContainer.children.forEach((monster: PIXI.DisplayObject) => {
                    if (monster.name !== monsterSymbol.name) {
                        featureTl.add([
                            (monster as MonsterSymbol).getStateAnimation(MONSTER_EXPRESSION.SCARED, 6, true),
                            () => {
                                Game.soundPlayer.playSound(monster.name + "Ducking" + MathHelper.randomIntInRange(1, 3), 1);
                            }
                        ], "scared");
                    }
                });

                struckMonsters.forEach((monster: MonsterSymbol) => {
                    if (monster.name !== monsterSymbol.name) {
                        featureTl.add((monster as MonsterSymbol).getStateAnimation(MONSTER_WEAPON_ACTIONS.AS_HIT, 5), "hitAction");
                        bigExplodeTl.add(monster.playBigExplode(0.5), 0);
                    }
                });

                if (monstersToExplode.includes(monsterSymbol.name)) {
                    featureTl.add(monsterSymbol.getStateAnimation(MONSTER_EXPRESSION.FLIP_THE_BIRD, 6), "hitAction");
                }

                featureTl.addLabel("fly");
                Game.monsterReelsView.monsterSymbolContainer.children.forEach((monster: PIXI.DisplayObject) => {
                    if (monster.name !== monsterSymbol.name && !monstersToExplode.includes(monster.name)) {
                        featureTl.add([
                            (monster as MonsterSymbol).getStateAnimation(MONSTER_EXPRESSION.LAUGH, 6),
                            () => {
                                Game.soundPlayer.playSound(monsterSymbol.name + "Laugh" + MathHelper.randomIntInRange(1, 3), 1);
                            }
                        ], "fly");
                    } else if (monster.name === monsterSymbol.name && !monstersToExplode.includes(monsterSymbol.name)) {
                        featureTl.add([
                            monsterSymbol.getStateAnimation(MONSTER_EXPRESSION.LAUGH, 6),
                            () => {
                                Game.soundPlayer.playSound(monsterSymbol.name + "Laugh" + MathHelper.randomIntInRange(1, 3), 1);
                            }
                        ], "fly");
                    }
                });
                featureTl.add(monsterSymbol.playAirstrikeFly(airstrikes), "fly");

                break;
            }
        }

        featureTl.addLabel("doExplosion");
        featureTl.add(bigExplodeTl, "doExplosion");
        featureTl.add(() => {
            if (Game.getCurrentGameMode() === GameMode.NORMAL) {
                Game.soundPlayer.fadeInAndStartSound({
                    name: Sound.AS_SNARE,
                    from: 1,
                    to: 0,
                    duration: 4000,
                    repeat: Number.MAX_VALUE,
                    isResume: true
                });
            }
        }, "<");

        airstrikes.forEach((airstrike: IMonsterWalk) => {
            Game.updateSurprisedMonstersInExplosion(airstrike.x, 0, Game.ACTIVE_ROWS_NUM);

            for (let reelId: number = 0; reelId < Game.ACTIVE_REELS_NUM; reelId++) {
                Game.updateSurprisedMonstersInExplosion(reelId, airstrike.y, airstrike.y + 1);
            }
        });

        airstrikes.forEach((airstrike: IMonsterWalk, idx: number) => {
            const airstrikeSymbol: Symbol = struckMonsters[idx];
            featureTl.add(Game.explodeSymbols(airstrike.x, 0, Game.ACTIVE_ROWS_NUM, true), "doExplosion");
            featureTl.add(Game.symbolFloors.showExplosionMark(airstrike.x, airstrike.y), "<0.2");

            for (let reelId: number = 0; reelId < Game.ACTIVE_REELS_NUM; reelId++) {
                featureTl.add(Game.explodeSymbols(reelId, airstrike.y, airstrike.y + 1, true), "doExplosion");
            }

            if (Game.canRemoveSymbol(airstrikeSymbol)) {
                featureTl.add(Game.reelsView.reelModels[airstrike.x].removeSymbol(airstrikeSymbol), "doExplosion");
                featureTl.add(() => {
                    airstrikeSymbol.isRemoved = true;
                });
            }
        });

        if (Game.model.data.maxExpansion){
            featureTl.add(Game.explodeSymbolsBeforeMaxExpansion(), "<0.3");

            featureTl.add(Game.expandGridForMaxExpansion(), "<0.5");
        }

        if (!this._isAbort){
            featureTl.add(()=>{
                Game.shakeScreen()
            }, "<");
        }
    }

    private onSkip(tl: gsap.core.Timeline): void {
        const carriedSymbolUserNode: IPosition = Game.model.data.carriedSymbolUserNode;
        const monsterSymbol: MonsterSymbol = Game.reelsView.reelModels[carriedSymbolUserNode.x].getMonsterSymbolAtRowId(carriedSymbolUserNode.y);
        Game.soundPlayer.stopSound(Sound.AS_ACTIVATE);
        Game.soundPlayer.stopSound("attackAnticipation");

        Game.soundPlayer.fadeInAndStartSound({
            name: Sound.AS_SNARE,
            from: 1,
            to: 0,
            duration: 0,
            repeat: Number.MAX_VALUE,
            isResume: true
        });

        this._isAbort = true;

        tl.totalProgress(1);

        if (monsterSymbol) {
            Game.soundPlayer.stopSound(monsterSymbol.name + "AirstrikeGoodbye1");
            Game.soundPlayer.stopSound(monsterSymbol.name + "AirstrikeGoodbye2");
            Game.soundPlayer.stopSound(monsterSymbol.name + "AirstrikeGoodbye3");
        }

        // #444
        Game.monsterReelsView.monsterSymbolContainer.children.forEach((monsterSymbol: PIXI.DisplayObject) => {
            (monsterSymbol as MonsterSymbol).skip();
        });
    }

    private getStruckMonsters(airstrikes: IMonsterWalk[]): {
        struckMonsters: MonsterSymbol[]
    } {
        const struckMonsters: MonsterSymbol[] = [];
        let struckSym: Symbol,
            reelsSymName: string;

        airstrikes.forEach((airstrike: IMonsterWalk) => {
            reelsSymName = Game.model.gameData.evaluatedArea[airstrike.x][airstrike.y];
            struckSym = Game.reelsView.reelModels[airstrike.x].getSymbolAt(airstrike.y, reelsSymName);

            if ((reelsSymName !== "MX") && (struckSym) && (Game.MONSTER_SYMBOLS.includes(struckSym.name))) {
                struckMonsters.push(struckSym as MonsterSymbol);
            }
        })

        return {struckMonsters};
    }
}